/**
 * useResourceForm - store-direct form logic for Resource editing.
 *
 * Reads from the Resource store, writes directly to the Resource store.
 * No local state duplication -- the store IS the source of truth.
 *
 * All store-write callbacks use a ref for resourceId to avoid stale closures.
 */

import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useResourceById, useResourceActions } from '../stores';
import { getResourceById } from './resourceStore';
import type { ResourceDefinition, ResourceField, ResourceFieldType, ResourceAggregation } from '../types';

export interface ResourceForm {
  name: string;
  setName: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  scopeType: 'global' | 'board' | 'canvas';
  setScopeType: (v: 'global' | 'board' | 'canvas') => void;
  color: string;
  setColor: (v: string) => void;
  fields: ResourceField[];
  addField: () => void;
  updateField: (fieldId: string, updates: Partial<ResourceField>) => void;
  removeField: (fieldId: string) => void;

  loadedResourceId: string | null;
  existingResource: ResourceDefinition | undefined;
  canSave: boolean;

  deleteResource: () => boolean;
}

export function useResourceForm(resourceId?: string): ResourceForm {
  const { t } = useTranslation();
  const resource = useResourceById(resourceId || '');
  const actions = useResourceActions();

  // ---- Ref for resourceId -- all callbacks read from here, never stale ----
  const resourceIdRef = useRef(resourceId);
  resourceIdRef.current = resourceId;

  // ---- Name: local buffer for controlled input + write-through ----
  const [localName, setLocalName] = useState(resource?.name ?? '');

  const prevResourceIdForName = useRef(resourceId);
  if (prevResourceIdForName.current !== resourceId) {
    prevResourceIdForName.current = resourceId;
    setLocalName(resource?.name ?? '');
  }

  // ---- Store writes (all use resourceIdRef -- never stale) ----

  const update = useCallback((updates: Partial<Omit<ResourceDefinition, 'id' | 'createdAt'>>) => {
    const id = resourceIdRef.current;
    if (!id) return;
    actions.updateResource(id, updates);
  }, [actions]);

  const setName = useCallback((v: string) => {
    setLocalName(v);
    update({ name: v });
  }, [update]);

  const setCategory = useCallback((v: string) => update({ category: v }), [update]);

  const setColor = useCallback((v: string) => update({ color: v }), [update]);

  const setScopeType = useCallback((v: 'global' | 'board' | 'canvas') => {
    const id = resourceIdRef.current;
    if (!id) return;
    const current = getResourceById(id);
    if (!current) return;
    const boardId = current.scope.type !== 'global' ? (current.scope as any).boardId : '';
    const treeId = current.scope.type === 'canvas' ? (current.scope as any).treeId : '';
    let scope: ResourceDefinition['scope'];
    if (v === 'global') scope = { type: 'global' };
    else if (v === 'board') scope = { type: 'board', boardId };
    else scope = { type: 'canvas', boardId, treeId };
    update({ scope });
  }, [update]);

  // ---- Fields ----

  const addField = useCallback(() => {
    const id = resourceIdRef.current;
    if (!id) return;
    const current = getResourceById(id);
    if (!current) return;
    const newField: ResourceField = {
      id: `fld-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      key: '',
      value: 0,
      type: 'number' as ResourceFieldType,
      aggregate: 'sum' as ResourceAggregation,
      showOnBadge: false,
    };
    update({ fields: [...current.fields, newField] });
  }, [update]);

  const updateField = useCallback((fieldId: string, updates: Partial<ResourceField>) => {
    const id = resourceIdRef.current;
    if (!id) return;
    const current = getResourceById(id);
    if (!current) return;
    const updatedFields = current.fields.map(f =>
      f.id === fieldId ? { ...f, ...updates } : f
    );
    update({ fields: updatedFields });
  }, [update]);

  const removeField = useCallback((fieldId: string) => {
    const id = resourceIdRef.current;
    if (!id) return;
    const current = getResourceById(id);
    if (!current) return;
    update({ fields: current.fields.filter(f => f.id !== fieldId) });
  }, [update]);

  // ---- Delete ----

  const deleteResource = useCallback((): boolean => {
    const id = resourceIdRef.current;
    if (!id) return false;
    if (!confirm(t('resource.deleteConfirm'))) return false;
    actions.deleteResource(id);
    return true;
  }, [actions, t]);

  return {
    name: localName,
    setName,
    category: resource?.category ?? '',
    setCategory,
    scopeType: resource?.scope.type ?? 'global',
    setScopeType,
    color: resource?.color ?? '#6366f1',
    setColor,
    fields: resource?.fields ?? [],
    addField,
    updateField,
    removeField,
    loadedResourceId: resourceId ?? null,
    existingResource: resource,
    canSave: localName.trim() !== '',
    deleteResource,
  };
}
